// Service Worker Registration for 70Lives Business Finance Tracker
// This file handles PWA service worker registration and updates

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

// Register service worker for offline + update notifications
export function register(config) {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  if (!('serviceWorker' in navigator)) {
    return;
  }

  const swUrl = '/sw.js';

  if (isLocalhost) {
    // On localhost, check validity to avoid cache issues during dev.
    checkValidServiceWorker(swUrl, config);

    navigator.serviceWorker.ready.then(() => {
      console.log('Service worker is ready.');
    });
  } else {
    // Normal production register after confirming the worker script is valid.
    checkValidServiceWorker(swUrl, config);
  }
}

async function registerValidSW(swUrl, config) {
  try {
    const existingRegistration = await navigator.serviceWorker.getRegistration('/');
    const activeScriptUrl = existingRegistration?.active?.scriptURL || '';

    if (activeScriptUrl && !activeScriptUrl.endsWith('/sw.js')) {
      console.log('Migrating existing service worker to /sw.js:', activeScriptUrl);
    }
  } catch (error) {
    console.warn('Unable to inspect existing service worker registration:', error);
  }

  navigator.serviceWorker
    .register(swUrl, { scope: '/' })
    .then((registration) => {
      console.log('SW registered: ', registration);

      if (registration.waiting && navigator.serviceWorker.controller) {
        if (config && config.onUpdate) {
          config.onUpdate(registration);
        }
        showUpdateNotification(registration);
      }
      
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log(
                'New content is available and will be used when all ' +
                'tabs for this page are closed. See https://cra.link/PWA.'
              );

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
              
              // Show update notification
              showUpdateNotification(registration);
            } else {
              console.log('Content is cached for offline use.');

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
    cache: 'no-store',
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        console.error('Invalid service worker response:', response.status, contentType);
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.'
      );
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// Show update notification when new version is available
function showUpdateNotification(registration) {
  if (document.getElementById('update-banner')) {
    return;
  }

  // Create a simple notification
  const updateBanner = document.createElement('div');
  updateBanner.id = 'update-banner';
  updateBanner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #8B5CF6;
    color: white;
    padding: 12px;
    text-align: center;
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  `;
  
  updateBanner.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; gap: 16px;">
      <span>🎉 New version available!</span>
      <button id="update-btn" style="
        background: white;
        color: #8B5CF6;
        border: none;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
      " aria-label="Update app now">Update Now</button>
      <button id="dismiss-btn" style="
        background: transparent;
        color: white;
        border: 1px solid white;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
      " aria-label="Dismiss update notification">Later</button>
    </div>
  `;
  
  document.body.appendChild(updateBanner);

  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) {
      return;
    }
    refreshing = true;
    window.location.reload();
  });
  
  // Handle update button click
  document.getElementById('update-btn').addEventListener('click', () => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  });
  
  // Handle dismiss button click
  document.getElementById('dismiss-btn').addEventListener('click', () => {
    updateBanner.remove();
  });
  
  // Auto-dismiss after 10 seconds
  setTimeout(() => {
    if (document.getElementById('update-banner')) {
      updateBanner.remove();
    }
  }, 10000);
}

// Handle iOS install prompt
export function handleIOSInstallPrompt() {
  // Check if app is already installed
  if (window.navigator.standalone) {
    return false; // Already installed
  }
  
  // Check if it's iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isInStandaloneMode = window.navigator.standalone;
  
  if (isIOS && !isInStandaloneMode) {
    // Show iOS install instructions
    showIOSInstallInstructions();
    return true;
  }
  
  return false;
}

function showIOSInstallInstructions() {
  console.log('🚀 Showing iOS install instructions');
  
  // Check if banner already exists
  if (document.getElementById('ios-install-banner')) {
    console.warn('iOS install banner already exists');
    return;
  }

  // Create wrapper with everything inside (simpler approach)
  const wrapper = document.createElement('div');
  wrapper.id = 'ios-install-wrapper';
  wrapper.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: 99998;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
  `;

  // Create banner content
  const banner = document.createElement('div');
  banner.id = 'ios-install-banner';
  banner.style.cssText = `
    background: linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%);
    color: white;
    padding: 24px;
    border-radius: 16px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    box-shadow: 0 20px 25px rgba(0,0,0,0.3);
    width: 100%;
    max-width: 340px;
    text-align: center;
    position: relative;
    pointer-events: auto;
  `;
  
  // Build HTML content
  const contentHTML = `
    <div style="position: relative;">
      <!-- Close button -->
      <div id="ios-close-btn" role="button" tabindex="0" aria-label="Close install instructions" style="
        position: absolute;
        top: 0;
        right: 0;
        background: rgba(255,255,255,0.2);
        border: none;
        color: white;
        font-size: 24px;
        width: 44px;
        height: 44px;
        cursor: pointer;
        border-radius: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
        pointer-events: auto;
      ">✕</div>
      
      <!-- Title -->
      <div style="margin-bottom: 12px; margin-top: 24px;">
        <div style="font-size: 12px; opacity: 0.85; margin-bottom: 4px; letter-spacing: 0.5px;">QUICK SETUP</div>
        <div style="font-size: 24px; font-weight: 700; margin-bottom: 8px;">📱 Install App</div>
        <div style="font-size: 14px; opacity: 0.9;">Get the full app experience</div>
      </div>
      
      <!-- Instructions -->
      <div style="text-align: left; background: rgba(255,255,255,0.12); padding: 16px; border-radius: 12px; margin: 20px 0; font-size: 14px; line-height: 1.8;">
        <div style="margin-bottom: 12px; display: flex; align-items: flex-start;">
          <span style="
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: white;
            color: #8B5CF6;
            border-radius: 50%;
            font-weight: 700;
            margin-right: 12px;
            flex-shrink: 0;
          ">1</span>
          <span>Tap the <strong>Share</strong> button</span>
        </div>
        <div style="display: flex; align-items: flex-start;">
          <span style="
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: white;
            color: #8B5CF6;
            border-radius: 50%;
            font-weight: 700;
            margin-right: 12px;
            flex-shrink: 0;
          ">2</span>
          <span>Tap <strong>Add to Home Screen</strong></span>
        </div>
      </div>
      
      <!-- Button -->
      <button id="ios-dismiss-btn" type="button" style="
        width: 100%;
        background: white;
        color: #8B5CF6;
        border: none;
        padding: 16px 20px;
        border-radius: 10px;
        cursor: pointer;
        font-weight: 600;
        font-size: 16px;
        margin-top: 16px;
        margin-bottom: 8px;
        pointer-events: auto;
      ">Got it!</button>
    </div>
  `;
  
  banner.innerHTML = contentHTML;
  wrapper.appendChild(banner);
  
  // Add wrapper to body
  document.body.appendChild(wrapper);
  
  console.log('✅ Banner DOM added to body');
  
  // Cleanup function - CRITICAL!
  const cleanupBanner = () => {
    console.log('🧹 Cleaning up banner...');
    try {
      const w = document.getElementById('ios-install-wrapper');
      if (w) {
        w.style.opacity = '0';
        w.style.transition = 'opacity 0.3s ease';
        setTimeout(() => {
          if (w && w.parentNode) {
            w.remove();
          }
        }, 300);
      }
      console.log('✅ Banner removed');
    } catch (e) {
      console.error('Error cleaning up:', e);
    }
  };

  // Get button elements
  const dismissBtn = banner.querySelector('#ios-dismiss-btn');
  const closeBtn = banner.querySelector('#ios-close-btn');
  
  console.log('🔍 Button elements found:', { dismissBtn: !!dismissBtn, closeBtn: !!closeBtn });

  // Set up event handlers - BOTH click and touchend
  if (dismissBtn) {
    const handleDismissClick = (e) => {
      console.log('✅ DISMISS BUTTON CLICKED');
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      try {
        localStorage.setItem('ios-install-dismissed', 'true');
      } catch (e) {
        console.error('Storage error:', e);
      }
      cleanupBanner();
    };
    
    dismissBtn.addEventListener('click', handleDismissClick, { passive: false });
    dismissBtn.addEventListener('touchend', handleDismissClick, { passive: false });
    console.log('✅ Dismiss button listeners attached');
  }

  if (closeBtn) {
    const handleCloseClick = (e) => {
      console.log('✅ CLOSE BUTTON CLICKED');
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      try {
        localStorage.setItem('ios-install-dismissed', 'true');
      } catch (e) {
        console.error('Storage error:', e);
      }
      cleanupBanner();
    };
    
    closeBtn.addEventListener('click', handleCloseClick, { passive: false });
    closeBtn.addEventListener('touchend', handleCloseClick, { passive: false });
    closeBtn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleCloseClick(e);
      }
    });
    console.log('✅ Close button listeners attached');
  }

  // Wrapper click outside to close
  wrapper.addEventListener('click', (e) => {
    if (e.target === wrapper) {
      console.log('✅ Background clicked - closing');
      try {
        localStorage.setItem('ios-install-dismissed', 'true');
      } catch (e) {
        console.error('Storage error:', e);
      }
      cleanupBanner();
    }
  });
  
  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    console.log('⏱️ Auto-dismiss timeout reached');
    cleanupBanner();
  }, 30000);
  
  console.log('🎉 iOS install banner READY');
}

// Check for app updates periodically
export function checkForUpdates() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.update();
    });
  }
}

// Initialize PWA features
export function initializePWA() {
  // Keep install prompts hidden while the standalone experience is improved.
  try {
    localStorage.setItem('ios-install-dismissed', 'true');
  } catch (e) {
    console.warn('Unable to persist iOS install dismissal flag:', e);
  }

  console.log('PWA install banners are disabled; please use Chrome for the best experience.');

  // Check for updates every 30 minutes
  setInterval(checkForUpdates, 30 * 60 * 1000);
  
  // Handle app visibility changes
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      checkForUpdates();
    }
  });
}
